import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { liveLinkStatusService, LiveLinkStatus } from '../../services/liveLinkStatusService';

interface EditLinkStatusModalProps {
  open: boolean;
  linkStatus: LiveLinkStatus | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditLinkStatusModal: React.FC<EditLinkStatusModalProps> = ({
  open,
  linkStatus,
  onClose,
  onSuccess,
}) => {
  const [status, setStatus] = useState<LiveLinkStatus['status']>('PENDING');
  const [statusMessage, setStatusMessage] = useState('');
  const [nextCheckAt, setNextCheckAt] = useState('');

  useEffect(() => {
    if (linkStatus) {
      setStatus(linkStatus.status);
      setStatusMessage(linkStatus.statusMessage || '');
      
      // Format nextCheckAt for datetime-local input
      if (linkStatus.nextCheckAt) {
        const date = new Date(linkStatus.nextCheckAt);
        const formatted = date.toISOString().slice(0, 16);
        setNextCheckAt(formatted);
      } else {
        // Default to 15 days from now
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 15);
        setNextCheckAt(futureDate.toISOString().slice(0, 16));
      }
    }
  }, [linkStatus]);

  const updateMutation = useMutation({
    mutationFn: (data: { status: string; statusMessage?: string; nextCheckAt?: string }) =>
      liveLinkStatusService.updateLinkStatus(linkStatus!.id, data),
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = () => {
    updateMutation.mutate({
      status,
      statusMessage: statusMessage || undefined,
      nextCheckAt: nextCheckAt ? new Date(nextCheckAt).toISOString() : undefined,
    });
  };

  if (!linkStatus) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        }
      }}
    >
      <DialogTitle sx={{ 
        fontSize: '18px', 
        fontWeight: 700, 
        color: '#111827',
        borderBottom: '1px solid #E5E7EB',
        pb: 2
      }}>
        Edit Link Status
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Live URL"
            value={linkStatus.liveUrl}
            disabled
            variant="filled"
            sx={{
              '& .MuiFilledInput-root': {
                borderRadius: '8px',
                fontSize: '13px',
                backgroundColor: '#F9FAFB',
                '&:before, &:after': {
                  display: 'none'
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '13px',
                fontWeight: 500
              }
            }}
          />

          <FormControl fullWidth>
            <InputLabel sx={{ fontSize: '13px', fontWeight: 500 }}>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as LiveLinkStatus['status'])}
              sx={{
                borderRadius: '8px',
                fontSize: '13px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E5E7EB'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D1D5DB'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4F46E5',
                  borderWidth: '2px'
                }
              }}
            >
              <MenuItem value="PENDING" sx={{ fontSize: '13px' }}>Pending</MenuItem>
              <MenuItem value="CHECKING" sx={{ fontSize: '13px' }}>Checking</MenuItem>
              <MenuItem value="OK_200" sx={{ fontSize: '13px' }}>OK (200)</MenuItem>
              <MenuItem value="ERROR_4XX" sx={{ fontSize: '13px' }}>4xx Error</MenuItem>
              <MenuItem value="ERROR_5XX" sx={{ fontSize: '13px' }}>5xx Error</MenuItem>
              <MenuItem value="TIMEOUT" sx={{ fontSize: '13px' }}>Timeout</MenuItem>
              <MenuItem value="UNKNOWN" sx={{ fontSize: '13px' }}>Unknown</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Status Message"
            value={statusMessage}
            onChange={(e) => setStatusMessage(e.target.value)}
            multiline
            rows={2}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '13px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D1D5DB'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4F46E5',
                  borderWidth: '2px'
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '13px',
                fontWeight: 500
              }
            }}
          />

          <TextField
            fullWidth
            label="Next Check Date"
            type="datetime-local"
            value={nextCheckAt}
            onChange={(e) => setNextCheckAt(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '13px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D1D5DB'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4F46E5',
                  borderWidth: '2px'
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '13px',
                fontWeight: 500
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        borderTop: '1px solid #E5E7EB', 
        px: 3, 
        py: 2,
        gap: 1
      }}>
        <Button 
          onClick={onClose}
          sx={{
            color: '#374151',
            fontWeight: 500,
            fontSize: '13px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#F9FAFB'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={updateMutation.isPending}
          sx={{
            backgroundColor: '#4F46E5',
            fontWeight: 500,
            fontSize: '13px',
            textTransform: 'none',
            boxShadow: '0 1px 2px rgba(79,70,229,0.25)',
            '&:hover': {
              backgroundColor: '#4338CA',
              boxShadow: '0 3px 8px rgba(79,70,229,0.3)'
            },
            '&:disabled': {
              backgroundColor: '#E5E7EB',
              color: '#9CA3AF'
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLinkStatusModal;
